/**
 * Create Collection - unified composer for standard, graph, and library collections.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TextEditor from "components/TextEditor";
import LibraryCollectionForm, {
  type LibraryDraftItem,
} from "components/Posts/LibraryCollectionForm";
import PostFormShell from "components/Posts/PostFormShell";
import MediaSection from "components/Posts/sections/MediaSection";
import LinkField from "components/Posts/sections/LinkField";
import TitleField from "components/Posts/sections/TitleField";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import api from "config/axiosConfig";
import { collectionApi, mediaAssetsApi } from "lib/api";
import { collectionCreateSchema, collectionEditSchema } from "lib/validation/contentSchemas";
import { useAuthGate } from "hooks/useAuthGate";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import { useMediaManager } from "hooks/useMediaManager";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { useAutoSaveDraft } from "hooks/useAutoSaveDraft";
import { useUrlTitlePrefill } from "hooks/useUrlTitlePrefill";
import { useUser } from "util/UserContext";
import { buildDraftStorageKey } from "util/draftStorage";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { apiRoutes } from "util/apiRoutes";
import {
  addCoauthors,
  createCollection,
  type CollectionPostResponse,
} from "util/posts";
import { standardizeUrl } from "util/textUtils";
import { PrivacyLevel } from "util/userUtils";
import { getCollectionUiStrings, resolveCollectionLocale } from "i18n/collectionUi";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";

export interface CollectionFormHandle {
  submit: () => void;
  saveDraft: () => Promise<void>;
  discardDraft: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (collection: CollectionPostResponse) => void;
  onCancel: () => void;
  hideActions?: boolean;
  /**
   * When true, the form omits its built-in title field so the caller can
   * render the title somewhere else (e.g. as the DetailPanel's always-
   * visible header in the graph editor). The caller is responsible for
   * syncing the title state via `initialCollection.title` + its own patch
   * flow. Defaults to false.
   */
  hideTitle?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /** Merged into `properties` on create (e.g. graph collection metadata). */
  extraCollectionProperties?: Record<string, unknown>;
  mode?: "create" | "edit";
  collectionId?: number | null;
  initialCollection?: CollectionPostResponse | null;
  initialDraftValue?: Record<string, unknown> | null;
  onDirtyChange?: (isDirty: boolean) => void;
}

type CollectionType = "standard" | "graph" | "library";

function normalizeCollectionType(value: unknown): CollectionType {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "graph") {
    return "graph";
  }
  if (normalized === "library") {
    return "library";
  }
  return "standard";
}

const CollectionPostForm = forwardRef<CollectionFormHandle, Props>(
  (
    {
      entity,
      onSuccess,
      onCancel,
      hideActions = false,
      hideTitle = false,
      onCanSubmitChange,
      onSubmittingChange,
      extraCollectionProperties,
      mode = "create",
      collectionId = null,
      initialCollection = null,
      initialDraftValue = null,
      onDirtyChange,
    },
    ref
  ) => {
    const ui = getCollectionUiStrings(resolveCollectionLocale());
    const draftUi = getDraftUiStrings(resolveDraftLocale());
    const { currentUser } = useAuthGate();
    const { setShowAuthModal } = useUser();
    const { resolvedIssue: collectionIssue } = useResolvedIssue(entity);
    const isEditMode =
      mode === "edit" && Number.isFinite(Number(collectionId)) && Number(collectionId) > 0;
    const lockedCollectionType = normalizeCollectionType(extraCollectionProperties?.collection_type);
    const collectionTypeLocked =
      typeof extraCollectionProperties?.collection_type === "string" &&
      extraCollectionProperties.collection_type.trim().length > 0;

    const [privacy, setPrivacy] = useState<PrivacyLevel>("me");
    const { coauthors, setCoauthors, addCoauthor, removeCoauthor, coauthorPairs } =
      useCoauthorManager(privacy);

    const mm = useMediaManager(20);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [referenceLink, setReferenceLink] = useState("");
    const [titleValidationMessage] = useState("");
    const [showLinkField, setShowLinkField] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [collectionType, setCollectionType] = useState<CollectionType>(lockedCollectionType);
    const [libraryItems, setLibraryItems] = useState<LibraryDraftItem[]>([]);
    const initialTitleRef = useRef("");
    const initialDescriptionRef = useRef("");
    const initialReferenceLinkRef = useRef("");
    const initialShowLinkFieldRef = useRef(false);
    const initialPrivacyRef = useRef<PrivacyLevel>("me");
    const initialCollectionTypeRef = useRef<CollectionType>(lockedCollectionType);

    const isLibraryCollection = collectionType === "library";

    useEffect(() => {
      if (collectionTypeLocked) {
        setCollectionType(lockedCollectionType);
      }
    }, [collectionTypeLocked, lockedCollectionType]);

    useEffect(() => {
      if (!initialDraftValue || isEditMode) {
        return;
      }
      setTitle(typeof initialDraftValue.title === "string" ? initialDraftValue.title : "");
      setDescription(typeof initialDraftValue.description === "string" ? initialDraftValue.description : "");
      setReferenceLink(typeof initialDraftValue.referenceLink === "string" ? initialDraftValue.referenceLink : "");
      setShowLinkField(Boolean(initialDraftValue.showLinkField));
      const nextPrivacy =
        typeof initialDraftValue.privacy === "string" && initialDraftValue.privacy.trim()
          ? initialDraftValue.privacy
          : "me";
      setPrivacy(nextPrivacy as PrivacyLevel);
      if (!collectionTypeLocked) {
        setCollectionType(normalizeCollectionType(initialDraftValue.collectionType));
      }
      const nextCoauthors = initialDraftValue.coauthors;
      setCoauthors(Array.isArray(nextCoauthors) ? nextCoauthors as Parameters<typeof setCoauthors>[0] : []);
    }, [collectionTypeLocked, initialDraftValue, isEditMode, setCoauthors]);

    useEffect(() => {
      if (!isEditMode || !initialCollection) {
        return;
      }

      const nextCollectionType = collectionTypeLocked
        ? lockedCollectionType
        : normalizeCollectionType(initialCollection.properties?.collection_type);
      const nextPrivacy = (String(initialCollection.properties?.privacy || "me").trim().toLowerCase() ||
        "me") as PrivacyLevel;
      const nextTitle = initialCollection.title || "";
      const nextDescription = initialCollection.body || "";
      const nextReferenceLink = initialCollection.url || "";
      const nextShowLinkField = Boolean(initialCollection.url);

      initialTitleRef.current = nextTitle;
      initialDescriptionRef.current = nextDescription;
      initialReferenceLinkRef.current = nextReferenceLink;
      initialShowLinkFieldRef.current = nextShowLinkField;
      initialPrivacyRef.current = nextPrivacy;
      initialCollectionTypeRef.current = nextCollectionType;

      setTitle(nextTitle);
      setDescription(nextDescription);
      setReferenceLink(nextReferenceLink);
      setShowLinkField(nextShowLinkField);
      setPrivacy(nextPrivacy);
      setCollectionType(nextCollectionType);
    }, [collectionTypeLocked, initialCollection, isEditMode, lockedCollectionType]);

    const { loading } = useUrlTitlePrefill({
      linkEnabled: showLinkField && !isLibraryCollection,
      referenceLink,
      titleToggleRequiredForPrefill: false,
      titleEnabled: true,
      setTitle,
    });

    const lifecycle = usePostFormLifecycle({
      getValues: useCallback(
        () => ({
          showLinkField: showLinkField && !isLibraryCollection,
          referenceLink,
          title,
          description,
          mediaCount: isLibraryCollection ? libraryItems.length : mm.media.length,
          hasIssue: Boolean(collectionIssue?.id),
        }),
        [
          collectionIssue,
          description,
          isLibraryCollection,
          libraryItems.length,
          mm.media.length,
          referenceLink,
          showLinkField,
          title,
        ]
      ),
      // In edit mode, title is optional (drafts may be named later). The
      // calling surface enforces title-on-publish separately. `isEditMode`
      // is reactive because it derives from `collectionId`, so the schema
      // swap happens as soon as create becomes edit.
      schema: isEditMode ? collectionEditSchema : collectionCreateSchema,
      isReady: true,
      onSubmittingChange,
    });

    const canSubmit =
      lifecycle.canSubmit && (!isLibraryCollection || libraryItems.length > 0);

    useEffect(() => {
      onCanSubmitChange?.(canSubmit);
    }, [canSubmit, onCanSubmitChange]);

    const collectionDraftKey = currentUser
      ? buildDraftStorageKey("draft", "collection", "new", currentUser.id, collectionIssue?.id ?? "none")
      : null;

    const { clearDraft } = useAutoSaveDraft({
      draftKey: collectionDraftKey,
      value: {
        collectionType,
        coauthors,
        description,
        privacy,
        referenceLink,
        showLinkField,
        title,
      },
      enabled: Boolean(currentUser) && !isEditMode,
      restoreMode: "none",
      isDirty:
        title.trim().length > 0 ||
        description.trim().length > 0 ||
        referenceLink.trim().length > 0 ||
        showLinkField ||
        coauthors.length > 0 ||
        privacy !== "me" ||
        collectionType !== "standard",
      restoreMessage: ui.restoreDraft,
      onRestore: (draft) => {
        setTitle(draft.title || "");
        setDescription(draft.description || "");
        setReferenceLink(draft.referenceLink || "");
        setShowLinkField(Boolean(draft.showLinkField));
        setPrivacy((draft.privacy as PrivacyLevel) || "me");
        setCoauthors(Array.isArray(draft.coauthors) ? draft.coauthors : []);
        if (!collectionTypeLocked) {
          setCollectionType(normalizeCollectionType(draft.collectionType));
        }
      },
      shouldSave: (draft) =>
        Boolean(
          draft.title.trim().length > 0 ||
            draft.description.trim().length > 0 ||
            draft.referenceLink.trim().length > 0 ||
            draft.showLinkField ||
            draft.coauthors.length > 0 ||
            draft.privacy !== "me" ||
            draft.collectionType !== "standard"
        ),
    });

    const isDirty = useMemo(() => {
      if (!isEditMode) {
        return false;
      }

      return (
        title !== initialTitleRef.current ||
        description !== initialDescriptionRef.current ||
        referenceLink !== initialReferenceLinkRef.current ||
        showLinkField !== initialShowLinkFieldRef.current ||
        privacy !== initialPrivacyRef.current ||
        collectionType !== initialCollectionTypeRef.current ||
        mm.media.length > 0
      );
    }, [collectionType, description, isEditMode, mm.media.length, privacy, referenceLink, showLinkField, title]);

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const uploadMediaFiles = useCallback(
      async (collectionId: number) => {
        if (mm.media.length === 0) {
          return;
        }
        setIsUploadingMedia(true);
        try {
          await uploadEntityMediaDirect(
            "collection",
            collectionId,
            mm.media.map((mediaFile) => ({
              file: mediaFile.file,
              order: mediaFile.order,
              intrinsic: mediaFile.intrinsic,
            }))
          );
        } finally {
          setIsUploadingMedia(false);
        }
      },
      [mm.media]
    );

    const attachLibraryItems = useCallback(
      async (collectionId: number) => {
        const orderedItems = [...libraryItems].sort((left, right) => left.sortOrder - right.sortOrder);
        const uploadItems = orderedItems.filter(
          (
            item
          ): item is LibraryDraftItem & {
            kind: "media";
            source: "upload";
            file: File;
          } =>
            item.kind === "media" && item.source === "upload" && item.file instanceof File
        );
        const uploadedAssets = await mediaAssetsApi.uploadStandalone(
          uploadItems.map((item) => ({
            file: item.file,
            order: item.sortOrder,
          })),
          {
            audience: privacy,
            properties: {
              collection_id: collectionId,
              collection_type: "library",
            },
          }
        );

        let uploadIndex = 0;
        for (const item of orderedItems) {
          if (item.kind === "media") {
            const mediaAssetId =
              item.source === "existing"
                ? item.entityId
                : uploadedAssets[uploadIndex++]?.id;
            if (!mediaAssetId) {
              throw new Error("LIBRARY_MEDIA_ATTACH_FAILED");
            }
            await collectionApi.addItem(collectionId, {
              entityId: mediaAssetId,
              entityType: "media_asset",
              properties: { sort_order: item.sortOrder },
            });
            continue;
          }

          await collectionApi.addItem(collectionId, {
            entityId: item.entityId,
            entityType: "url_metadata",
            properties: { sort_order: item.sortOrder },
          });
        }
      },
      [libraryItems, privacy]
    );

    const submit = useCallback(async () => {
      if (!currentUser) {
        setShowAuthModal(true);
        return;
      }

      const validationError = lifecycle.validateOrError();
      if (validationError) {
        lifecycle.setError(validationError);
        return;
      }
      if (isLibraryCollection && libraryItems.length === 0) {
        lifecycle.setError(ui.libraryItemsRequired);
        return;
      }
      if (!canSubmit) {
        return;
      }

      lifecycle.beginSubmit();

      try {
        const trimmedLink = referenceLink.trim();
        const finalLink =
          !isLibraryCollection && showLinkField && trimmedLink
            ? standardizeUrl(trimmedLink)
            : null;
        const finalTitle = title.trim();
        if (isEditMode && collectionId) {
          const response = await api.patch<CollectionPostResponse>(apiRoutes.posts.byId(collectionId), {
            title: finalTitle || initialCollection?.title || null,
            body: description.trim() || null,
            url: finalLink,
            privacy,
            properties: {
              privacy,
              collection_type: collectionType,
              ...(extraCollectionProperties || {}),
            },
          });

          if (mm.media.length > 0) {
            await uploadMediaFiles(Number(collectionId));
          }

          const updatedCollection = {
            ...response.data,
            title: finalTitle || initialCollection?.title || response.data.title || null,
            body: description.trim() || null,
            url: finalLink,
            properties: {
              ...(response.data.properties || {}),
              privacy,
              collection_type: collectionType,
              ...(extraCollectionProperties || {}),
            },
          };

          initialTitleRef.current = updatedCollection.title || "";
          initialDescriptionRef.current = updatedCollection.body || "";
          initialReferenceLinkRef.current = updatedCollection.url || "";
          initialShowLinkFieldRef.current = Boolean(updatedCollection.url);
          initialPrivacyRef.current = privacy;
          initialCollectionTypeRef.current = collectionType;

          toast.success(ui.updateSuccess);
          onSuccess(updatedCollection);
          return;
        }

        const created = await createCollection({
          issueId: collectionIssue?.id ?? null,
          title: finalTitle,
          body: description.trim() || null,
          url: finalLink,
          audience: privacy,
          properties: {
            privacy,
            collection_type: collectionType,
            ...(extraCollectionProperties || {}),
          },
        });

        if (coauthors.length > 0 && privacy !== "anonymous") {
          try {
            await addCoauthors("collection", created.id, coauthorPairs);
          } catch {
            /* best-effort */
          }
        }

        if (isLibraryCollection) {
          await attachLibraryItems(created.id);
        } else if (mm.media.length > 0) {
          await uploadMediaFiles(created.id);
        }

        toast.success(ui.createSuccess);
        clearDraft();
        onSuccess(created);
      } catch {
        toast.error(isEditMode ? ui.updateError : ui.createError);
      } finally {
        lifecycle.endSubmit();
      }
    }, [
      attachLibraryItems,
      canSubmit,
      clearDraft,
      coauthorPairs,
      coauthors.length,
      collectionIssue,
      collectionType,
      currentUser,
      description,
      extraCollectionProperties,
      initialCollection?.title,
      isEditMode,
      isLibraryCollection,
      libraryItems.length,
      lifecycle,
      mm.media.length,
      collectionId,
      onSuccess,
      privacy,
      referenceLink,
      setShowAuthModal,
      showLinkField,
      title,
      ui.updateError,
      ui.updateSuccess,
      ui.createError,
      ui.createSuccess,
      ui.libraryItemsRequired,
      uploadMediaFiles,
    ]);

    const saveDraft = useCallback(async () => {
      if (!currentUser) {
        setShowAuthModal(true);
        return;
      }
      const trimmedLink = referenceLink.trim();
      const finalLink =
        !isLibraryCollection && showLinkField && trimmedLink
          ? standardizeUrl(trimmedLink)
          : null;
      await createCollection({
        status: "draft",
        issueId: collectionIssue?.id ?? null,
        title: title.trim() || null,
        body: description.trim() || null,
        url: finalLink,
        audience: privacy,
        properties: {
          privacy,
          collection_type: collectionType,
          ...(extraCollectionProperties || {}),
          draft_library_items: libraryItems,
        },
      });
      clearDraft();
      toast.success(draftUi.draftSaved);
    }, [
      clearDraft,
      collectionIssue?.id,
      collectionType,
      currentUser,
      description,
      extraCollectionProperties,
      isLibraryCollection,
      libraryItems,
      privacy,
      referenceLink,
      setShowAuthModal,
      showLinkField,
      title,
      draftUi.draftSaved,
    ]);

    useImperativeHandle(ref, () => ({ submit: () => void submit(), saveDraft, discardDraft: clearDraft }), [clearDraft, saveDraft, submit]);

    const linkField = useMemo(
      () => (
        <LinkField
          id="collection-reference-link"
          name="collection-reference-link"
          labelText={ui.referenceUrlLabel}
          value={referenceLink}
          onChange={setReferenceLink}
        />
      ),
      [referenceLink, ui.referenceUrlLabel]
    );

    const titleField = useMemo(
      () => (
        <TitleField
          id="collection-name"
          name="collection-name"
          labelText={ui.collectionNameLabel}
          fieldRequired={!isEditMode}
          value={title}
          onChange={setTitle}
          maxLength={300}
          loading={loading}
          error={titleValidationMessage}
          validationState={
            titleValidationMessage ? "invalid" : title.trim().length > 0 ? "valid" : "none"
          }
        />
      ),
      [isEditMode, loading, title, titleValidationMessage, ui.collectionNameLabel]
    );

    return (
      <PostFormShell intro={isLibraryCollection ? ui.introLibrary : ui.introStandard}>
        {!collectionTypeLocked ? (
          <label className="block text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
            {ui.collectionTypeLabel}
            <select
              className="mt-1 w-full rounded-lg border border-eoe-border-main bg-eoe-bg px-3 py-2 text-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
              value={collectionType}
              onChange={(event) =>
                setCollectionType(normalizeCollectionType(event.target.value))
              }
            >
              <option value="standard">{ui.collectionTypeStandard}</option>
              <option value="graph">{ui.collectionTypeGraph}</option>
              <option value="library">{ui.collectionTypeLibrary}</option>
            </select>
          </label>
        ) : null}

        {!isLibraryCollection ? (
          <MediaSection
            idPrefix="collection"
            mm={mm}
            showLink={showLinkField}
            showTitle={!hideTitle}
            onToggleLink={() => {
              if (showLinkField) {
                setShowLinkField(false);
                setReferenceLink("");
                return;
              }
              setShowLinkField(true);
            }}
            onToggleTitle={() => {}}
            linkField={linkField}
            titleField={hideTitle ? null : titleField}
            allowTitleToggle={false}
          />
        ) : (
          <div className="space-y-4">
            {hideTitle ? null : titleField}
            <LibraryCollectionForm
              items={libraryItems}
              onItemsChange={setLibraryItems}
              audience={privacy}
              disabled={lifecycle.isSubmitting}
            />
          </div>
        )}

        <div className="mt-0">
          <TextEditor
            value={description}
            isEditorExpanded={true}
            onChange={setDescription}
            editorType="entity"
            placeholder={ui.bodyPlaceholder}
            fieldRequired={false}
          />
        </div>

        {privacy !== "anonymous" && !isEditMode ? (
          <CoauthorSection
            coauthors={coauthors}
            onAdd={addCoauthor}
            onRemove={removeCoauthor}
            placeholder={ui.addPeoplePlaceholder}
          />
        ) : null}

        <PrivacySection privacy={privacy} onPrivacyChange={setPrivacy} miniIcon={false} showDropdown={true} />

        <FormStatusMessage error={lifecycle.error} />

        {!hideActions ? (
          <div className="flex flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
            <button type="button" onClick={onCancel} className="btn-secondary w-full px-4 py-2 sm:w-fit">
              {ui.cancel}
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isUploadingMedia}
              onClick={(event) => {
                event.preventDefault();
                void submit();
              }}
              className={`w-full px-4 py-2 sm:w-fit ${
                !canSubmit || isUploadingMedia ? "btn-primary-disabled" : "btn-primary"
              } ${lifecycle.isSubmitting || isUploadingMedia ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {lifecycle.isSubmitting || isUploadingMedia ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                </span>
              ) : (
                isEditMode ? ui.save : ui.submit
              )}
            </button>
          </div>
        ) : null}
      </PostFormShell>
    );
  }
);

CollectionPostForm.displayName = "CollectionPostForm";

export default CollectionPostForm;
